import type { SelectOption, SelectOptionGroup } from '../select/select.tsx';
import type { MeetingTemplateEditorLabels } from './meeting-template-editor-labels.ts';
import type {
  MeetingTemplateCohortOption,
  MeetingTemplateTrackOption,
} from './meeting-template-types.ts';

/** Chosen to offer the template to the whole organisation. */
export const AUDIENCE_NONE = '';

/**
 * One dropdown answers "for whom" with a track or a cohort, so its values say
 * which of the two they name.
 */
export function audienceChoice(ids: {
  trackId?: string | null;
  cohortId?: string | null;
}): string {
  if (ids.trackId) return `track:${ids.trackId}`;
  if (ids.cohortId) return `cohort:${ids.cohortId}`;

  return AUDIENCE_NONE;
}

/** The ids a dropdown value stands for. */
export function audienceIds(choice: string): {
  trackId: string | null;
  cohortId: string | null;
} {
  const [kind, id] = choice.split(':');

  return {
    trackId: kind === 'track' ? id : null,
    cohortId: kind === 'cohort' ? id : null,
  };
}

/**
 * The "for whom" answers, one group per track so the reader sees which cohorts
 * run it. A track without cohorts still gets its own entry: picking it reaches
 * the cohorts added later. Cohorts on no track come last.
 */
export function templateAudienceOptions(
  tracks: MeetingTemplateTrackOption[],
  cohorts: MeetingTemplateCohortOption[],
  labels: Pick<
    MeetingTemplateEditorLabels,
    'audienceNone' | 'audienceWholeTrack' | 'audienceOnlyCohort' | 'audienceOtherCohorts'
  >,
): (SelectOption | SelectOptionGroup)[] {
  const cohortOption = (cohort: MeetingTemplateCohortOption): SelectOption => ({
    value: audienceChoice({ cohortId: cohort.id }),
    label: labels.audienceOnlyCohort({ title: cohort.title }),
  });

  const trackIds = new Set(tracks.map((track) => track.id));
  const loose = cohorts.filter(
    (cohort) => cohort.trackId === null || !trackIds.has(cohort.trackId),
  );

  return [
    { value: AUDIENCE_NONE, label: labels.audienceNone },
    ...tracks.map((track) => ({
      label: track.title,
      options: [
        {
          value: audienceChoice({ trackId: track.id }),
          label: labels.audienceWholeTrack({ title: track.title }),
        },
        ...cohorts.filter((cohort) => cohort.trackId === track.id).map(cohortOption),
      ],
    })),
    ...(loose.length > 0
      ? [{ label: labels.audienceOtherCohorts, options: loose.map(cohortOption) }]
      : []),
  ];
}
